import React, { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/AppContext';
import { ERROR_KEYS } from '../utils/errorUtils';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const from = (location.state as { from?: string } | null)?.from;
  const { login, error: authError } = useAuth();

  const resolveAuthError = (err: string | null) => {
    if (!err) return '';
    if (err === ERROR_KEYS.NETWORK) return t('errors.network_issue');
    if (err === ERROR_KEYS.INVALID_CREDENTIALS) return t('login.invalid_credentials');
    if (err === ERROR_KEYS.GENERIC) return t('login.error_generic');
    return t('login.error_generic');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError('');
    setIsSubmitting(true);
    try {
      const success = await login(email, password);
      if (success) {
        navigate(from && from.startsWith('/dashboard') ? from : '/dashboard', { replace: true });
      } else {
        setLocalError(t('login.invalid_credentials'));
      }
    } catch (err) {
      setLocalError(t('login.error_generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = localError || resolveAuthError(authError);

  return (
    <div className="page-wrapper">
      <div className="common-page-grid">
        <div className="page-background">
          <div className="image-wrap">
            <img 
              src="/images/Inner-Page-BG.webp" 
              loading="lazy" 
              sizes="(max-width: 2880px) 100vw, 2880px"
              alt="" 
              className="img"
            />
          </div>
        </div>
        <div className="common-page-content">
          <div className="log-content-wrap">
            <a href="/" className="navbar-brand w-inline-block">
              <img src="/images/Logo.png" loading="lazy" alt="CONVPILOT Logo" className="logo" />
            </a>
            <div className="w-form">
              <form onSubmit={handleSubmit} className="log-in-form">
                <div className="log-in-heading">
                  <div className="text-default">{t('login.enter_details')}</div>
                </div>
                <div className="log-button-wrapper">
                  <div className="log-input-wrapper">
                    <div className="log-field-wrap">
                      <label htmlFor="Email" className="text-default">{t('login.email')}</label>
                      <input
                        className="log-in-feild w-input"
                        maxLength={256}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('login.email_placeholder')}
                        type="email"
                        id="Email"
                        required
                      />
                    </div>
                    <div className="log-field-wrap">
                      <label htmlFor="Password" className="text-default">{t('login.password')}</label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'stretch' }}>
                        <input
                          className="log-in-feild w-input"
                          maxLength={256}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder=""
                          type={showPassword ? 'text' : 'password'}
                          id="Password"
                          required
                          style={{ paddingRight: '44px' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          title={showPassword ? 'Hide password' : 'Show password'}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 44,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'rgba(255,255,255,0.6)',
                            padding: 0,
                          }}
                        >
                          {showPassword ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  {displayError && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '14px',
                      marginTop: '10px',
                      textAlign: 'center'
                    }}>
                      {displayError}
                    </div>
                  )}
                  <div className="log-button-wrap">
                    <button
                      type="submit"
                      className="button-primary-inner submit w-button"
                      disabled={isSubmitting}
                      style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                    >
                      {isSubmitting ? t('login.signing_in') : t('login.log_in')}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

